import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AdminClient, Employee } from "@/hooks/useAdminAuth";
import Pagination from "@/components/base/Pagination";
import {
  COUNTRIES, SERVICE_PLANS, JOINT_STIM_PLANS, CLIENT_SOURCES, formatServicePlan, formatDate,
} from "@/pages/admin/clients/constants";

interface ClientTableProps {
  clients: AdminClient[];
  count: number;
  loading: boolean;
  page: number;
  pageSize: number;
  search: string;
  countryFilter: string;
  servicePlanFilter: string;
  clientSourceFilter: string;
  isSingleFilter: string;
  progressStatusFilter: string;
  advisorFilter: string;
  jointStimFilter: string;
  hasGeneticFilter: string;
  geneticTypeFilter: string;
  followupSort: "asc" | "desc" | null;
  followups: Record<number, string | null>;
  advisors: Employee[];
  canAssign: boolean;
  canDelete: boolean;
  isSuperAdmin: boolean;
  selectedIds: Set<number>;
  setSearch: (v: string) => void;
  setCountryFilter: (v: string) => void;
  setServicePlanFilter: (v: string) => void;
  setClientSourceFilter: (v: string) => void;
  setIsSingleFilter: (v: string) => void;
  setProgressStatusFilter: (v: string) => void;
  setAdvisorFilter: (v: string) => void;
  setJointStimFilter: (v: string) => void;
  setHasGeneticFilter: (v: string) => void;
  setGeneticTypeFilter: (v: string) => void;
  setPage: (v: number) => void;
  setPageSize: (v: number) => void;
  setFollowupSort: (v: "asc" | "desc" | null) => void;
  setSelectedIds: (v: Set<number> | ((p: Set<number>) => Set<number>)) => void;
  onSearch: () => void;
  onExportCSV: () => void;
  onCreate: () => void;
  onDetail: (c: AdminClient) => void;
  onEdit: (c: AdminClient) => void;
  onAssign: (c: AdminClient) => void;
  onDelete: (id: number) => void;
  onBatchAssign: () => void;
  onBatchDelete: () => void;
  copiedField: { id: number; field: string } | null;
  onCopy: (text: string, clientId: number, field: string) => void;
}

export default function ClientTable({
  clients, count, loading, page, pageSize, search, countryFilter, servicePlanFilter,
  clientSourceFilter, isSingleFilter, progressStatusFilter, advisorFilter,
  jointStimFilter, hasGeneticFilter, geneticTypeFilter, followupSort, followups,
  advisors, canAssign, canDelete, isSuperAdmin, selectedIds,
  setSearch, setCountryFilter, setServicePlanFilter, setClientSourceFilter,
  setIsSingleFilter, setProgressStatusFilter, setAdvisorFilter, setJointStimFilter,
  setHasGeneticFilter, setGeneticTypeFilter, setPage, setPageSize, setFollowupSort,
  setSelectedIds, onSearch, onExportCSV, onCreate, onDetail, onEdit, onAssign, onDelete,
  onBatchAssign, onBatchDelete, copiedField, onCopy,
}: ClientTableProps) {
  const navigate = useNavigate();
  const showBatchActions = (canAssign || canDelete) && selectedIds.size > 0;
  const showAdvisorCol = isSuperAdmin || canAssign;

  const getSortedClients = () => {
    if (!followupSort) return clients;
    return [...clients].sort((a, b) => {
      const fa = followups[a.id] || null;
      const fb = followups[b.id] || null;
      if (!fa && !fb) return 0;
      if (!fa) return 1;
      if (!fb) return -1;
      return followupSort === "desc"
        ? new Date(fb).getTime() - new Date(fa).getTime()
        : new Date(fa).getTime() - new Date(fb).getTime();
    });
  };

  const displayClients = (() => {
    const sorted = getSortedClients();
    if (jointStimFilter === "all") return sorted;
    return sorted.filter((c) =>
      jointStimFilter === "yes" ? c.joint_stimulation === true : c.joint_stimulation !== true
    );
  })();

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayClients.length && displayClients.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayClients.map((c) => c.id)));
    }
  };

  const clearAllFilters = () => {
    setCountryFilter("all");
    setServicePlanFilter("all");
    setClientSourceFilter("all");
    setIsSingleFilter("all");
    setProgressStatusFilter("all");
    setAdvisorFilter("all");
    setJointStimFilter("all");
    setHasGeneticFilter("all");
    setGeneticTypeFilter("all");
    setPage(0);
  };

  const showClearBtn = countryFilter !== "all" || servicePlanFilter !== "all"
    || clientSourceFilter !== "all" || isSingleFilter !== "all"
    || progressStatusFilter !== "all" || advisorFilter !== "all"
    || jointStimFilter !== "all" || hasGeneticFilter !== "all";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1a3a5c]">客户管理</h1>
          <p className="text-xs text-gray-400 mt-1">管理所有签约客户信息</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onExportCSV} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
            <i className="ri-file-excel-2-line"></i>导出CSV
          </button>
          <button onClick={onCreate} className="px-4 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
            <i className="ri-add-line"></i>新增客户
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="搜索姓名或客户编码"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
            />
          </div>
          <button onClick={onSearch} className="px-4 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-search-line mr-1"></i>搜索
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={countryFilter} onChange={(e) => { setCountryFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">全部国家</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={servicePlanFilter} onChange={(e) => { setServicePlanFilter(e.target.value); setJointStimFilter("all"); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">全部方案</option>
            {SERVICE_PLANS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {JOINT_STIM_PLANS.has(servicePlanFilter) && (
            <select value={jointStimFilter} onChange={(e) => { setJointStimFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">联合促排</option><option value="yes">是</option><option value="no">否</option>
            </select>
          )}
          <select value={clientSourceFilter} onChange={(e) => { setClientSourceFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">全部来源</option>
            {CLIENT_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={isSingleFilter} onChange={(e) => { setIsSingleFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">伴侣状态</option><option value="yes">单身</option><option value="no">伴侣</option>
          </select>
          <select value={progressStatusFilter} onChange={(e) => { setProgressStatusFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">全部进度</option><option value="active">在途客户</option><option value="completed">已结束</option>
          </select>
          <select value={hasGeneticFilter} onChange={(e) => { setHasGeneticFilter(e.target.value); setGeneticTypeFilter("all"); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">遗传物质</option><option value="yes">是</option><option value="no">否</option>
          </select>
          {hasGeneticFilter === "yes" && (
            <select value={geneticTypeFilter} onChange={(e) => { setGeneticTypeFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部类型</option><option value="sperm">精子</option><option value="egg">卵子</option><option value="embryo">胚胎</option>
            </select>
          )}
          {showAdvisorCol && (
            <select value={advisorFilter} onChange={(e) => { setAdvisorFilter(e.target.value); setPage(0); }} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部顾问</option>
              {advisors.map((e) => <option key={e.id} value={String(e.id)}>{e.name}</option>)}
            </select>
          )}
          {showClearBtn && (
            <button onClick={clearAllFilters} className="px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
              <i className="ri-close-line"></i>清除筛选
            </button>
          )}
        </div>
      </div>

      {showBatchActions && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-amber-800">已选 <strong className="text-base">{selectedIds.size}</strong> 项</span>
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-amber-600 hover:text-amber-800 cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-close-circle-line mr-1"></i>清除选择
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canAssign && (
              <button onClick={onBatchAssign} className="px-3 py-1.5 bg-[#c9a96e] text-white rounded-lg text-xs font-medium hover:bg-[#b8944f] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                <i className="ri-user-star-line"></i>批量分配顾问
              </button>
            )}
            {canDelete && (
              <button onClick={onBatchDelete} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                <i className="ri-delete-bin-line"></i>批量删除
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-280px)]">
          <table className="w-max text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {(canAssign || canDelete) && (
                  <th className="text-center px-3 py-3 text-xs font-medium text-gray-500 whitespace-nowrap w-10">
                    <input type="checkbox" checked={selectedIds.size === displayClients.length && displayClients.length > 0} onChange={toggleSelectAll} className="w-3.5 h-3.5 rounded border-gray-300 text-[#1a3a5c] cursor-pointer accent-[#1a3a5c]" />
                  </th>
                )}
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">序号</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">客户</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">客户编码</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">服务方案<i className="ri-lock-line text-[10px] text-[#c9a96e]" title="服务方案创建后不可修改"></i></span>
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">国家</th>
                {showAdvisorCol && <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">专属顾问</th>}
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">进度状态</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">创建时间</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap cursor-pointer select-none" onClick={() => setFollowupSort((p) => p === "desc" ? "asc" : p === "asc" ? null : "desc")}>
                  <span className="inline-flex items-center gap-1">最近跟进
                    <span className="inline-flex flex-col text-[8px] leading-[6px]">
                      <span className={followupSort === "asc" ? "text-[#1a3a5c]" : "text-gray-300"}>▲</span>
                      <span className={followupSort === "desc" ? "text-[#1a3a5c]" : "text-gray-300"}>▼</span>
                    </span>
                  </span>
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={showAdvisorCol ? (canAssign || canDelete ? 12 : 11) : (canAssign || canDelete ? 11 : 10)} className="px-4 py-8 text-center whitespace-nowrap"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={showAdvisorCol ? (canAssign || canDelete ? 12 : 11) : (canAssign || canDelete ? 11 : 10)} className="px-4 py-8 text-center text-gray-400 text-sm whitespace-nowrap"><i className="ri-inbox-line text-2xl mb-2 block"></i>暂无客户数据</td></tr>
              ) : (
                displayClients.map((c, idx) => {
                  const hasInProgress = c.current_stage_status === "in_progress" && c.current_stage_id;
                  const displayName = c.is_single === false && c.partner_name ? `${c.name || "-"} & ${c.partner_name}` : (c.name || "-");
                  return (
                    <tr key={c.id} className={`transition-colors ${(c as any).genetic_alert ? "bg-red-50/70 hover:bg-red-100/60" : "hover:bg-gray-50"}`}>
                      {(canAssign || canDelete) && (
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} className="w-3.5 h-3.5 rounded border-gray-300 text-[#1a3a5c] cursor-pointer accent-[#1a3a5c]" />
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{page * pageSize + idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-[#1a3a5c] whitespace-nowrap">
                        {displayName}
                        {(c as any).genetic_alert && <span className="ml-1.5 text-[10px] text-red-500 font-medium"><i className="ri-alert-fill"></i></span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="text-[#1a3a5c]">{((c as any).client_code || "-")}</span>
                          <button onClick={() => onCopy((c as any).client_code || "", c.id, "code")} className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center text-gray-300 hover:text-[#1a3a5c] transition-colors cursor-pointer" title="复制客户编码">
                            {copiedField?.id === c.id && copiedField?.field === "code" ? <i className="ri-check-line text-emerald-500 text-xs"></i> : <i className="ri-file-copy-line text-xs"></i>}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {formatServicePlan(c.service_plan)}
                          {c.joint_stimulation === true && (<span className="ml-1 text-[10px] text-[#1a3a5c]">· 联合促排</span>)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.country || "-"}</td>
                      {showAdvisorCol && (
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {c.advisor_employee_name || c.advisor_name ? (
                            <span className="flex items-center gap-1"><i className="ri-user-star-line text-[#c9a96e] text-xs"></i>{c.advisor_employee_name || c.advisor_name}</span>
                          ) : "-"}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          if (!c.current_stage_name || c.current_stage_name === "-") return <span className="text-[11px] text-gray-300">-</span>;
                          const isAllCompleted = (c as any).all_stages_completed === true;
                          const displayText = isAllCompleted ? "已结束" : c.current_stage_name;
                          const sid = hasInProgress ? c.current_stage_id : null;
                          const navUrl = isAllCompleted ? `/admin/progress?client_id=${c.id}` : (sid ? `/admin/progress?client_id=${c.id}&stage_id=${sid}` : `/admin/progress?client_id=${c.id}`);
                          return (
                            <span onClick={() => navigate(navUrl)} className={`text-[11px] px-2 py-0.5 rounded-full font-medium cursor-pointer hover:opacity-80 transition-opacity ${isAllCompleted ? "bg-[#c9a96e]/10 text-[#c9a96e]" : "bg-[#1a3a5c]/10 text-[#1a3a5c]"}`} title={isAllCompleted ? "所有阶段已完成" : `点击查看进度：${c.current_stage_name}`}>
                              {displayText}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{followups[c.id] ? formatDate(followups[c.id]) : "-"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => onDetail(c)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-[#1a3a5c]/10 flex items-center justify-center text-gray-400 hover:text-[#1a3a5c] transition-colors cursor-pointer" title="详情"><i className="ri-eye-line text-xs"></i></button>
                          <button onClick={() => onEdit(c)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-[#1a3a5c]/10 flex items-center justify-center text-gray-400 hover:text-[#1a3a5c] transition-colors cursor-pointer" title="编辑"><i className="ri-edit-line text-xs"></i></button>
                          {canAssign && (
                            <button onClick={() => onAssign(c)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-[#c9a96e]/10 flex items-center justify-center text-gray-400 hover:text-[#c9a96e] transition-colors cursor-pointer" title="分配顾问"><i className="ri-user-star-line text-xs"></i></button>
                          )}
                          {canDelete && (
                            <button onClick={() => onDelete(c.id)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer" title="删除"><i className="ri-delete-bin-line text-xs"></i></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {count > 0 && (
          <Pagination page={page} pageSize={pageSize} total={count} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(0); }} />
        )}
      </div>
    </div>
  );
}